import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Shared operation status UI components.
 *
 * Portable progress bar, error banner, and success screen for use
 * across all editors. Uses ac- prefixed CSS classes from editor-core
 * design tokens.
 */
import { useEffect, useRef } from 'react';
import { getOverallPercent, formatBytes } from '../types/operation-progress';
function formatDuration(ms) {
    const totalSeconds = Math.floor(ms / 1000);
    if (totalSeconds < 60)
        return `${totalSeconds}s`;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}
export function OperationProgressBar({ progress }) {
    const overallPercent = getOverallPercent(progress);
    const { currentStep, totalSteps, stepLabel, bytesSent, bytesTotal, bytesSentAllSteps, bytesTotalAllSteps } = progress;
    const stepPercent = bytesTotal > 0 ? Math.floor((bytesSent / bytesTotal) * 100) : 0;
    const startTimeRef = useRef(Date.now());
    const prevProgressRef = useRef(null);
    useEffect(() => {
        const prev = prevProgressRef.current;
        if (!prev || (progress.currentStep === 1 && progress.bytesSent === 0 && progress.bytesSentAllSteps === 0)) {
            startTimeRef.current = Date.now();
        }
        prevProgressRef.current = progress;
    }, [progress]);
    const elapsedMs = Date.now() - startTimeRef.current;
    const totalBytesSoFar = bytesSentAllSteps + bytesSent;
    const bytesRemaining = bytesTotalAllSteps - totalBytesSoFar;
    const bytesPerMs = elapsedMs > 500 && totalBytesSoFar > 0
        ? totalBytesSoFar / elapsedMs
        : 0;
    const estimatedRemainingMs = bytesPerMs > 0 ? bytesRemaining / bytesPerMs : 0;
    return (_jsxs("div", { className: "ac-operation-progress", children: [_jsxs("div", { className: "ac-operation-progress-header", children: [_jsxs("span", { className: "ac-operation-progress-label", children: [stepLabel, bytesTotal > 0 && _jsxs("span", { className: "ac-operation-progress-pct", children: [" ", stepPercent, "%"] })] }), _jsxs("span", { className: "ac-operation-progress-step", children: ["Step ", currentStep, " of ", totalSteps] })] }), _jsx("div", { className: "ac-operation-progress-track", children: _jsx("div", { className: "ac-operation-progress-fill", style: { width: `${overallPercent}%` } }) }), _jsxs("div", { className: "ac-operation-progress-footer", children: [bytesTotal > 0 ? (_jsxs("span", { children: [formatBytes(bytesSent), " / ", formatBytes(bytesTotal)] })) : (_jsx("span", {})), _jsxs("span", { children: ["Overall ", overallPercent, "%"] })] }), _jsxs("div", { className: "ac-operation-progress-footer", children: [_jsxs("span", { children: ["Elapsed: ", formatDuration(elapsedMs)] }), bytesPerMs > 0 && bytesRemaining > 0 && (_jsxs("span", { children: ["~", formatDuration(estimatedRemainingMs), " remaining"] }))] })] }));
}
export function OperationErrorBanner({ error }) {
    return (_jsx("div", { className: "ac-operation-error", "data-testid": "import-error", children: error }));
}
export function OperationSuccessScreen({ message, detail, onDone, }) {
    return (_jsxs("div", { className: "ac-operation-success", children: [_jsxs("div", { className: "ac-operation-success-message", children: [_jsx("svg", { className: "ac-operation-success-icon", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M5 13l4 4L19 7" }) }), _jsx("span", { children: message })] }), detail && _jsx("div", { className: "ac-operation-success-detail", children: detail }), _jsx("div", { className: "ac-operation-success-actions", children: _jsx("button", { onClick: onDone, className: "ac-btn ac-btn-primary", children: "Done" }) })] }));
}
export function OperationLoadingSpinner({ message }) {
    return (_jsxs("div", { className: "ac-operation-spinner", children: [_jsx("span", { className: "ac-spinner" }), _jsx("span", { children: message })] }));
}
export function OperationButtonContent({ isOperating, label, operatingLabel = 'Working...', }) {
    if (isOperating) {
        return (_jsxs(_Fragment, { children: [_jsx("span", { className: "ac-spinner ac-spinner-sm" }), operatingLabel] }));
    }
    return _jsx(_Fragment, { children: label });
}
//# sourceMappingURL=OperationStatus.js.map